// FTEDN Team — Login Page (Google Workspace Auth)
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Input } from '@ftedn/ui';
import { useAuth } from '../../hooks/useAuth';
import { getGoogleClientId } from '../../hooks/useApi';
import './LoginPage.css';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (el: HTMLElement, config: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function getGoogleErrorMessage(errorCode?: string, fallback?: string) {
  switch (errorCode) {
    case 'EMAIL_NOT_VERIFIED':
      return 'Google says this OOEDN email is not verified yet. Verify the account in Google, then try again.';
    case 'INVALID_AUDIENCE':
      return 'Google sign-in is using the wrong OAuth client. Reload, then contact admin if it continues.';
    case 'NOT_IN_ROSTER':
      return 'This OOEDN Google account is not on the FTEDN team roster.';
    case 'UNAUTHORIZED':
      return fallback || 'Use an authorized @ooedn.com Google account.';
    default:
      return fallback || 'Google sign-in failed. Try again or contact admin.';
  }
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(true);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const showEmailFallback = import.meta.env.DEV;

  const handleGoogleResponse = useCallback(async (response: { credential: string }) => {
    setError('');
    setLoading(true);
    const result = await loginWithGoogle(response.credential);
    setLoading(false);
    if (result.success) navigate('/');
    else setError(getGoogleErrorMessage(result.errorCode, result.message));
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    let mounted = true;

    async function initGoogle() {
      const clientId = await getGoogleClientId();
      if (!clientId || clientId.startsWith('PLACEHOLDER')) {
        if (mounted) setGoogleLoading(false);
        return;
      }

      // Load Google Identity Services script
      const existingScript = document.querySelector('script[src*="accounts.google.com/gsi"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (mounted) renderGoogleButton(clientId);
        };
        script.onerror = () => {
          if (mounted) setGoogleLoading(false);
        };
        document.head.appendChild(script);
      } else if (window.google?.accounts) {
        renderGoogleButton(clientId);
      } else {
        // Script exists but not loaded yet, wait
        existingScript.addEventListener('load', () => {
          if (mounted) renderGoogleButton(clientId);
        });
      }
    }

    function renderGoogleButton(clientId: string) {
      if (!window.google?.accounts || !googleBtnRef.current) {
        setGoogleLoading(false);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
        auto_select: false,
        itp_support: true,
      });

      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black',
        size: 'large',
        width: 320,
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
      });

      setGoogleReady(true);
      setGoogleLoading(false);
    }

    initGoogle();
    return () => { mounted = false; };
  }, [handleGoogleResponse]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your @ooedn.com email');
      return;
    }
    setError('');
    setLoading(true);
    const success = await login(email, '');
    setLoading(false);
    if (success) navigate('/');
    else setError('Not an authorized team member. Use your @ooedn.com email.');
  };

  return (
    <div className="login-container">
      <Card className="login-card" padding="lg" tone="elevated">
        <div className="login-header">
          <h1 className="login-logo"><span className="logo-f">f</span><span className="logo-dot">.</span></h1>
          <p className="login-brand-name">team dashboard</p>
          <p className="login-subtitle">OOEDN Workspace</p>
        </div>

        {/* Google Sign-In rendered here */}
        <div ref={googleBtnRef} className="google-btn-container" />

        {googleLoading && (
          <div className="google-loading">Loading Google Sign-In...</div>
        )}

        {!googleReady && !googleLoading && showEmailFallback && (
          <>
            <div className="login-divider"><span>sign in with team email</span></div>
            <form onSubmit={handleEmailLogin} className="login-form" id="login-form">
              <Input
                id="email"
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@ooedn.com"
                required
                type="email"
                value={email}
              />
              <Button fullWidth id="submit-btn" loading={loading} type="submit" variant="navy">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </>
        )}

        {!googleReady && !googleLoading && !showEmailFallback && (
          <p className="login-error login-error-bottom">Google Workspace sign-in is not configured.</p>
        )}

        {error && <p className="login-error login-error-bottom">{error}</p>}

        <div className="team-roster">
          <Badge className="roster-badge" tone="navy">Restricted</Badge>
          <p className="roster-label">Restricted to authorized OOEDN team members</p>
        </div>
      </Card>
    </div>
  );
}
